import React, { useState } from 'react';
import ChatInterface from './components/ChatInterface';
import ClassicModeRedesign from './components/ClassicModeRedesign/ClassicModeRedesign';
import AboutModal from './components/AboutModal';
import SplashScreen from './components/SplashScreen';
import { MessageSquare, Sparkles, Info } from 'lucide-react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { GoogleSignInButton } from './auth/CognitoAuth';
import DisclaimerModal from './components/DisclaimerModal';

function LoginPage() {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  return (
    <div
      className="flex flex-col items-center min-h-screen bg-gray-950 bg-cover bg-center relative pt-12"
      style={{ backgroundImage: 'url(/nebula-bg.png)' }}
    >
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-black/50" />
      <div className="flex flex-col items-center gap-3 px-10 py-4 rounded-2xl bg-gray-900/80 backdrop-blur-sm border border-gray-700 shadow-2xl max-w-md w-full mx-auto relative z-10">
        <div className="flex items-center gap-3">
          <img src="/LogoFinal.png?v=5" alt="RNAverse" className="w-10 h-10" />
          <div>
            <h1 className="text-xl font-bold text-white leading-tight">RNAverse</h1>
            <p className="text-gray-400 text-xs">
              AI-powered lncRNA therapeutic target discovery
            </p>
          </div>
        </div>

        {/* Consent checkbox */}
        <label className="flex items-start gap-3 cursor-pointer group w-full">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0 cursor-pointer flex-shrink-0"
          />
          <span className="text-xs text-gray-300 leading-relaxed select-none group-hover:text-gray-200 transition-colors">
            I confirm I am using RNAverse for non-commercial research purposes only
            and agree to{' '}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDisclaimer(true);
              }}
              className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2"
            >
              these terms
            </button>
            .
          </span>
        </label>

        <div className="w-full pt-2">
          <GoogleSignInButton disabled={!agreedToTerms} />
        </div>
      </div>

      {/* Disclaimer Modal */}
      {showDisclaimer && <DisclaimerModal onClose={() => setShowDisclaimer(false)} />}
    </div>
  );
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  const [mode, setMode] = useState<'chat' | 'pro'>('chat');
  const [showAbout, setShowAbout] = useState(false);
  const [showSplash, setShowSplash] = useState(() => {
    return !sessionStorage.getItem('rnaverse_splash_shown');
  });

  const handleSplashComplete = () => {
    sessionStorage.setItem('rnaverse_splash_shown', 'true');
    setShowSplash(false);
  };

  // Show splash video FIRST, before anything else
  if (showSplash) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-950">
        <div className="text-gray-400 text-sm">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <div className="App">
      {/* Mode Toggle */}
      <div className="fixed top-4 right-4 z-[9999]">
        <div className="bg-gray-900 rounded-lg shadow-md border border-gray-700 p-1 flex gap-1">
          <button
            onClick={() => setMode('chat')}
            className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded text-xs md:text-sm font-medium transition-all ${
              mode === 'chat'
                ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
                : 'text-gray-400 hover:bg-gray-800'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Chat</span>
          </button>
          <button
            onClick={() => setMode('pro')}
            className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded text-xs md:text-sm font-medium transition-all ${
              mode === 'pro'
                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                : 'text-gray-400 hover:bg-gray-800'
            }`}
            title="Professional Interface"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Pro</span>
          </button>
          <button
            onClick={() => setShowAbout(true)}
            className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded text-xs md:text-sm font-medium text-gray-400 hover:bg-gray-800 transition-all"
            title="About RNAverse"
          >
            <Info className="w-4 h-4" />
            <span className="hidden sm:inline">About</span>
          </button>
        </div>
      </div>

      {/* About Modal */}
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}

      {/* Main Content - Keep ChatInterface mounted to preserve state */}
      <div style={{ display: mode === 'chat' ? 'block' : 'none' }}>
        <ChatInterface />
      </div>
      {mode === 'pro' && <ClassicModeRedesign />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
